/**
 * WHYSHIFT enhanced cart drawer — gifts, protection, upsells, subscription upgrades.
 */
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';

const GIFT_PROP = '_whyshift_free_gift';
const PARENT_PROP = '_whyshift_parent_key';
const PROTECTION_PROP = '_whyshift_protection';

class WhyshiftCartDrawer {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #giftSyncInProgress = false;

  init() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const drawer = document.querySelector('cart-drawer-component.whyshift-cart-drawer--enhanced');
    drawer?.addEventListener('dialog:open', () => {
      this.#bindDrawer(drawer);
      this.#reconcileGiftsOnOpen();
    }, { signal });

    this.#bindDrawer(drawer);
    this.#loadConnectionUpsells(drawer);
  }

  /** @param {Element | null} root */
  #bindDrawer(root) {
    if (!root) return;

    root.querySelectorAll('[data-whyshift-protection-toggle]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.addEventListener('change', this.#onProtectionToggle);
      }
    });

    root.querySelectorAll('[data-whyshift-subscription-upgrade]').forEach((btn) => {
      btn.addEventListener('click', this.#onSubscriptionUpgrade);
    });

    root.querySelectorAll('[data-whyshift-upsell-add]').forEach((btn) => {
      btn.addEventListener('click', this.#onUpsellAdd);
    });
  }

  /** @param {CustomEvent} event */
  #onCartAdd = async (event) => {
    if (!this.#autoGiftEnabled()) return;
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source === 'whyshift-cart-drawer') return;

    const productId = event.detail?.data?.productId;
    const productForm = productId
      ? document.querySelector(`product-form-component[data-product-id="${productId}"]`)
      : null;
    const giftVariantId =
      this.#parseVariantId(productForm?.getAttribute('data-gift-variant-id')) ??
      this.#parseVariantId(document.documentElement.dataset.whyshiftGiftVariantId);

    if (!giftVariantId) return;

    const cart = event.detail?.resource;
    if (!cart?.items) return;

    const variantId = event.detail?.sourceId;
    const parentItem = this.#findParentLineForGift(cart, variantId);
    if (!parentItem) return;

    const existingGift = cart.items.find(
      (/** @type {{ properties?: Record<string, string> }} */ item) =>
        item.properties?.[GIFT_PROP] === '1' && item.properties?.[PARENT_PROP] === parentItem.key
    );

    if (existingGift) {
      if (existingGift.quantity !== parentItem.quantity) {
        await this.#applyCartUpdates({ [existingGift.key]: parentItem.quantity });
      }
      return;
    }

    await this.#addGiftLine(giftVariantId, parentItem.key, parentItem.quantity);
  };

  /** @param {CustomEvent} event */
  #onCartUpdate = async (event) => {
    const cart = event.detail?.resource;
    if (!cart?.items) return;

    let activeCart = cart;

    if (this.#autoGiftEnabled() && event.detail?.data?.source !== 'whyshift-cart-drawer') {
      const syncedCart = await this.#syncGiftLines(cart);
      if (syncedCart) activeCart = syncedCart;
    }

    this.#updateRewardsBar(activeCart);

    const drawer = document.querySelector('cart-drawer-component.whyshift-cart-drawer--enhanced');
    this.#loadConnectionUpsells(drawer);
  };

  #autoGiftEnabled() {
    return document.documentElement.dataset.whyshiftCartAutoGift === 'true';
  }

  /** @param {string | null | undefined} raw */
  #parseVariantId(raw) {
    if (!raw) return null;
    const value = String(raw).trim();
    if (!value) return null;
    if (value.includes('/')) return value.split('/').pop() ?? null;
    return value;
  }

  /**
   * @param {{ items: Array<{ key: string, variant_id: number, quantity: number, properties?: Record<string, string> }> }} cart
   * @param {string | undefined} variantId
   */
  #findParentLineForGift(cart, variantId) {
    if (!variantId) {
      const nonGiftItems = cart.items.filter((item) => !item.properties?.[GIFT_PROP]);
      return nonGiftItems[nonGiftItems.length - 1] ?? null;
    }

    const candidates = cart.items.filter(
      (item) => String(item.variant_id) === String(variantId) && !item.properties?.[GIFT_PROP]
    );

    const withoutGift = candidates.find(
      (item) =>
        !cart.items.some(
          (gift) => gift.properties?.[GIFT_PROP] === '1' && gift.properties?.[PARENT_PROP] === item.key
        )
    );

    return withoutGift ?? candidates[candidates.length - 1] ?? null;
  }

  /**
   * @param {string} variantId
   * @param {string} parentKey
   * @param {number} [quantity]
   */
  async #addGiftLine(variantId, parentKey, quantity = 1) {
    const parsedVariantId = this.#parseVariantId(variantId);
    if (!parsedVariantId) return;

    const sectionIds = this.#getSectionIds();
    const body = {
      items: [
        {
          id: Number(parsedVariantId),
          quantity,
          properties: {
            [GIFT_PROP]: '1',
            [PARENT_PROP]: parentKey,
          },
        },
      ],
    };
    if (sectionIds) body.sections = sectionIds;

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

    if (response.status || response.errors) {
      console.warn('WHYSHIFT gift add failed:', response.description || response.message || response.errors);
      return;
    }

    document.dispatchEvent(
      new CartUpdateEvent(response, 'whyshift-cart-drawer', {
        source: 'whyshift-cart-drawer',
        sections: response.sections,
      })
    );
  }

  /**
   * @param {Record<string, number>} updates
   */
  async #applyCartUpdates(updates) {
    const keys = Object.keys(updates);
    if (keys.length === 0) return null;

    const sectionIds = this.#getSectionIds();
    const body = {
      updates,
      sections_url: window.location.pathname,
    };
    if (sectionIds) body.sections = sectionIds;

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

    if (response.errors) {
      console.warn('WHYSHIFT gift sync failed:', response.errors);
      return null;
    }

    document.dispatchEvent(
      new CartUpdateEvent(response, 'whyshift-cart-drawer', {
        source: 'whyshift-cart-drawer',
        sections: response.sections,
      })
    );

    return response;
  }

  /** @param {{ items: Array<{ key: string, quantity: number, properties?: Record<string, string> }> }} cart */
  async #syncGiftLines(cart) {
    if (this.#giftSyncInProgress) return null;

    this.#giftSyncInProgress = true;

    try {
      const updates = {};
      const giftItems = cart.items.filter((item) => item.properties?.[GIFT_PROP] === '1');
      const parentKeys = new Set(
        cart.items.filter((item) => !item.properties?.[GIFT_PROP]).map((item) => item.key)
      );
      const giftsByParent = new Map();

      for (const gift of giftItems) {
        const parentKey = gift.properties?.[PARENT_PROP];

        if (!parentKey || !parentKeys.has(parentKey)) {
          updates[gift.key] = 0;
          continue;
        }

        if (giftsByParent.has(parentKey)) {
          updates[gift.key] = 0;
          continue;
        }

        giftsByParent.set(parentKey, gift);

        const parent = cart.items.find((item) => item.key === parentKey);
        if (parent && gift.quantity !== parent.quantity) {
          updates[gift.key] = parent.quantity;
        }
      }

      return await this.#applyCartUpdates(updates);
    } finally {
      this.#giftSyncInProgress = false;
    }
  }

  async #reconcileGiftsOnOpen() {
    if (!this.#autoGiftEnabled() || this.#giftSyncInProgress) return;

    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      await this.#syncGiftLines(cart);
    } catch (error) {
      console.warn('WHYSHIFT gift reconcile failed:', error);
    }
  }

  /** @param {{ total_price: number, item_count: number }} cart */
  #updateRewardsBar(cart) {
    const rewards = document.querySelector('[data-whyshift-cart-rewards]');
    if (!rewards) return;

    const threshold = Number(rewards.dataset.threshold) || 7500;
    const pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
    rewards.style.setProperty('--ws-cart-rewards-progress', `${pct}%`);
    rewards.classList.toggle('whyshift-cart-rewards--unlocked', cart.total_price >= threshold);

    const countEl = document.querySelector('[data-whyshift-cart-count]');
    if (countEl) countEl.textContent = String(cart.item_count);
  }

  #getSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids.join(',');
  }

  /** @param {Event} event */
  #onProtectionToggle = async (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    const wrap = input.closest('[data-whyshift-cart-protection]');
    if (!wrap) return;

    const variantId = wrap.getAttribute('data-variant-id');
    if (!variantId) return;

    if (input.checked) {
      const data = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { [PROTECTION_PROP]: '1' } }],
          sections: this.#getSectionIds(),
        }),
      }).then((r) => r.json());

      document.dispatchEvent(
        new CartUpdateEvent(data, 'whyshift-cart-drawer', { source: 'whyshift-cart-drawer', sections: data.sections })
      );
    } else {
      const line = Number(wrap.getAttribute('data-line'));
      if (line > 0) {
        const data = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0, sections: this.#getSectionIds() }),
        }).then((r) => r.json());

        document.dispatchEvent(
          new CartUpdateEvent(data, 'whyshift-cart-drawer', { source: 'whyshift-cart-drawer', sections: data.sections })
        );
      }
    }
  };

  /** @param {Event} event */
  #onSubscriptionUpgrade = async (event) => {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const line = Number(btn.dataset.line);
    const sellingPlan = btn.dataset.sellingPlan;
    if (!line || !sellingPlan) return;

    btn.disabled = true;
    const data = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line,
        quantity: 1,
        selling_plan: Number(sellingPlan),
        sections: this.#getSectionIds(),
      }),
    }).then((r) => r.json());

    document.dispatchEvent(
      new CartUpdateEvent(data, 'whyshift-cart-drawer', { source: 'whyshift-cart-drawer', sections: data.sections })
    );
    btn.disabled = false;
  };

  /** @param {Event} event */
  #onUpsellAdd = async (event) => {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: this.#getSectionIds(),
      }),
    }).then((r) => r.json());

    document.dispatchEvent(
      new CartAddEvent(response, variantId, {
        source: 'whyshift-cart-drawer',
        sections: response.sections,
      })
    );
    btn.disabled = false;
  };

  /** @param {Element | null} root */
  async #loadConnectionUpsells(root) {
    const container = root?.querySelector('[data-whyshift-cart-upsells][data-needs-connections="true"]');
    if (!container || container.dataset.connectionsLoaded === 'true') return;

    const productIds = (container.dataset.cartProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const intent = container.dataset.intent || 'complementary';
    const limit = Number(container.dataset.limit) || 8;
    const cartIds = new Set(productIds.map(Number));
    const addLabel = container.dataset.addLabel || 'Add to cart';
    const heading = container.dataset.upsellHeading || '';

    let products = [];

    for (const productId of productIds) {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const data = await response.json();
        const filtered = (data.products || [])
          .map((/** @type {Record<string, unknown>} */ product) => this.#normalizeUpsellProduct(product))
          .filter(
            (/** @type {{ id: number }} */ product) => product?.id && !cartIds.has(product.id)
          );
        if (filtered.length > 0) {
          products = filtered.slice(0, limit);
          break;
        }
      } catch {
        continue;
      }
    }

    if (products.length === 0) {
      try {
        const fallback = JSON.parse(container.dataset.fallbackProducts || '[]');
        if (Array.isArray(fallback) && fallback.length > 0) {
          products = fallback;
        }
      } catch {
        products = [];
      }
    }

    container.dataset.connectionsLoaded = 'true';

    if (products.length === 0) {
      container.remove();
      return;
    }

    const slides = products
      .map((product) => this.#buildUpsellSlide(product, addLabel))
      .join('');

    container.innerHTML = `
      <div class="whyshift-carousel whyshift-cart-upsells__carousel" data-whyshift-carousel>
        ${heading ? `<p class="whyshift-carousel__title">${heading}</p>` : ''}
        <div class="whyshift-carousel__viewport">
          <ul class="whyshift-carousel__track" role="list">
            ${slides}
          </ul>
        </div>
      </div>
    `;

    container.classList.remove('whyshift-cart-upsells--pending');
    this.#bindDrawer(root);
  }

  /**
   * @param {{ title: string, url: string, image: string | null, price: number, compare_at_price: number | null, variant_id: number, available: boolean }} product
   * @param {string} addLabel
   */
  #buildUpsellSlide(product, addLabel) {
    const compare = product.compare_at_price
      ? `<s class="whyshift-cart-upsell__compare">${this.#formatMoney(product.compare_at_price)}</s>`
      : '';
    const image = product.image
      ? `<img src="${product.image}" alt="${this.#escapeHtml(product.title)}" class="whyshift-cart-upsell__image" loading="lazy" width="120" height="120">`
      : '';

    return `
      <li class="whyshift-carousel__slide whyshift-cart-upsell__slide">
        <article class="whyshift-cart-upsell__card">
          <a href="${product.url}" class="whyshift-cart-upsell__media">${image}</a>
          <div class="whyshift-cart-upsell__info">
            <a href="${product.url}" class="whyshift-cart-upsell__title">${this.#escapeHtml(product.title)}</a>
            <div class="whyshift-cart-upsell__prices">
              ${compare}
              <span class="whyshift-cart-upsell__price">${this.#formatMoney(product.price)}</span>
            </div>
            <button
              type="button"
              class="whyshift-cart-upsell__atc button"
              data-whyshift-upsell-add
              data-variant-id="${product.variant_id}"
              ${product.available ? '' : 'disabled'}
            >
              ${this.#escapeHtml(addLabel)}
            </button>
          </div>
        </article>
      </li>
    `;
  }

  /**
   * @param {Record<string, unknown>} product
   */
  #normalizeUpsellProduct(product) {
    if (product.variant_id) return product;

    const variants = /** @type {Array<Record<string, unknown>> | undefined} */ (product.variants);
    const variant = variants?.[0];

    return {
      id: product.id,
      title: product.title,
      url: product.url,
      image: product.image || product.featured_image || null,
      price: variant?.price ?? product.price,
      compare_at_price:
        variant?.compare_at_price && variant?.price && Number(variant.compare_at_price) > Number(variant.price)
          ? variant.compare_at_price
          : product.compare_at_price ?? null,
      variant_id: variant?.id,
      available: variant?.available ?? product.available ?? true,
    };
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || Theme?.moneyFormat);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  /** @param {string} value */
  #escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

const instance = new WhyshiftCartDrawer();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => instance.init());
} else {
  instance.init();
}

document.addEventListener('shopify:section:load', () => instance.init());

export default instance;
